import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { ConsentWarning } from '../ui/ConsentWarning';
import { UserIcon, CreditCardIcon, FileTextIcon, InfoIcon, UsersIcon, ChevronDownIcon, ChevronUpIcon, DollarSignIcon } from 'lucide-react';
import { UAE_DATA_CLUSTERS, getDataClusterIcon } from '../../constants/uaeStandards';
import { AnimatedScreen, staggerContainer, staggerItem } from '../ui/AnimatedScreen';
import { Tooltip } from '../ui/Tooltip';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
export interface Permission {
  id: string;
  label: string;
  icon: 'user' | 'card' | 'file' | 'info' | 'users';
}
export interface ConsentDetailsScreenProps {
  tppName: string;
  bankName: string;
  expiryDate: string;
  permissions: Permission[];
  onProceed: () => void;
  durationMonths?: number;
}
// Icon mapping for data clusters - VeloX branded
const dataClusterIconMap: Record<string, ComponentType<any>> = {
  User: UserIcon,
  DollarSign: DollarSignIcon,
  CreditCard: CreditCardIcon,
  Info: InfoIcon,
  Users: UsersIcon,
  FileText: FileTextIcon
};
// Helper to get icon component for a data cluster
function getIconComponent(iconName?: string): ComponentType<any> {
  if (!iconName) return FileTextIcon;
  return dataClusterIconMap[iconName] || FileTextIcon;
}
export function ConsentDetailsScreen({
  tppName,
  bankName,
  expiryDate,
  permissions,
  onProceed,
  durationMonths = 6
}: ConsentDetailsScreenProps) {
  const [expandedClusters, setExpandedClusters] = useState<string[]>([]);
  const toggleCluster = (cluster: string) => {
    setExpandedClusters(prev => prev.includes(cluster) ? prev.filter(c => c !== cluster) : [...prev, cluster]);
  };
  return <AnimatedScreen data-id="element-493">
      <div className="space-y-6" data-id="element-494">
        <div data-id="element-495">
          <h2 className="text-xl font-bold mb-2 leading-tight" style={{
          fontFamily: OPENFINANCE_THEME.fonts.display,
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-496">
            {tppName} wants to access your data
          </h2>
          <p className="text-sm leading-relaxed" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-497">
            Review the data that will be shared with {tppName} from your{' '}
            {bankName} account
          </p>
        </div>

        {/* TPP Information - CDCS-2.6 with OpenFinance-OS styling */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="p-4 border-2" style={{
        background: OPENFINANCE_THEME.gradients.card,
        borderColor: OPENFINANCE_THEME.colors.primary200,
        borderRadius: OPENFINANCE_THEME.radius.lg
      }} data-id="element-498">
          <div className="text-xs font-medium uppercase tracking-wide mb-2" style={{
          color: OPENFINANCE_THEME.colors.primary500
        }} data-id="element-499">
            Service Provider Details
          </div>
          <div className="space-y-1 text-sm" data-id="element-500">
            <div className="flex justify-between" data-id="element-501">
              <span style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-502">
                Trading as:
              </span>
              <span className="font-semibold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-503">
                {tppName}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-504">
              <span style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-505">
                Legal entity:
              </span>
              <span className="font-semibold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-506">
                {tppName} Financial Services LLC
              </span>
            </div>
            <div className="flex justify-between" data-id="element-507">
              <span style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-508">
                Regulated by:
              </span>
              <span className="font-semibold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-509">
                UAE Central Bank
              </span>
            </div>
          </div>
        </motion.div>

        {/* Data Clusters with OpenFinance-OS styling and VeloX branded icons */}
        <div className="space-y-3" data-id="element-510">
          <div className="text-sm font-bold uppercase tracking-wide" style={{
          color: OPENFINANCE_THEME.colors.text,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-511">
            Data You're Sharing
          </div>

          <motion.div variants={staggerContainer} initial="hidden" animate="show" className="space-y-3" data-id="element-512">
            {UAE_DATA_CLUSTERS.slice(0, 5).map(dataCluster => {
            const isExpanded = expandedClusters.includes(dataCluster.cluster);
            const IconComponent = getIconComponent(dataCluster.icon);
            return <motion.div key={dataCluster.cluster} variants={staggerItem} className="border-2 overflow-hidden bg-white" style={{
              borderColor: OPENFINANCE_THEME.colors.border,
              borderRadius: OPENFINANCE_THEME.radius.lg
            }} data-id="element-513">
                  <button onClick={() => toggleCluster(dataCluster.cluster)} className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-514">
                    <div className="flex items-center gap-3 text-left" data-id="element-515">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: OPENFINANCE_THEME.colors.primary50,
                    borderRadius: OPENFINANCE_THEME.radius.md
                  }} data-id="element-516">
                        <IconComponent className="w-5 h-5" style={{
                      color: OPENFINANCE_THEME.colors.primary500
                    }} data-id="element-517" />
                      </div>
                      <div data-id="element-518">
                        <div className="font-semibold text-sm flex items-center gap-2" style={{
                      color: OPENFINANCE_THEME.colors.text
                    }} data-id="element-519">
                          {dataCluster.cluster}
                          <Tooltip content={`Includes ${dataCluster.permissions.length} specific permissions related to ${dataCluster.cluster.toLowerCase()}`} data-id="element-520">
                            <InfoIcon className="w-4 h-4 cursor-help" style={{
                          color: OPENFINANCE_THEME.colors.textMuted
                        }} data-id="element-521" />
                          </Tooltip>
                        </div>
                        <div className="text-xs" style={{
                      color: OPENFINANCE_THEME.colors.textMuted
                    }} data-id="element-522">
                          {dataCluster.permissions.length} permission
                          {dataCluster.permissions.length > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <motion.div animate={{
                  rotate: isExpanded ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-523">
                      <ChevronDownIcon className="w-5 h-5" style={{
                    color: OPENFINANCE_THEME.colors.textMuted
                  }} data-id="element-524" />
                    </motion.div>
                  </button>

                  <motion.div initial={false} animate={{
                height: isExpanded ? 'auto' : 0,
                opacity: isExpanded ? 1 : 0
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-525">
                    <div className="border-t-2 p-4 space-y-3" style={{
                  borderColor: OPENFINANCE_THEME.colors.borderLight,
                  backgroundColor: OPENFINANCE_THEME.colors.primary50
                }} data-id="element-526">
                      {dataCluster.permissions.map(permission => <div key={permission.id} className="space-y-1" data-id="element-527">
                          <div className="text-sm font-medium" style={{
                      color: OPENFINANCE_THEME.colors.text
                    }} data-id="element-528">
                            {permission.language}
                          </div>
                          <div className="text-xs" style={{
                      color: OPENFINANCE_THEME.colors.textLight
                    }} data-id="element-529">
                            Examples: {permission.examples}
                          </div>
                        </div>)}
                    </div>
                  </motion.div>
                </motion.div>;
          })}
          </motion.div>

          <button onClick={() => {
          if (expandedClusters.length === UAE_DATA_CLUSTERS.length) {
            setExpandedClusters([]);
          } else {
            setExpandedClusters(UAE_DATA_CLUSTERS.map(c => c.cluster));
          }
        }} className="text-sm font-medium transition-colors" style={{
          color: OPENFINANCE_THEME.colors.primary500
        }} data-id="element-530">
            {expandedClusters.length === UAE_DATA_CLUSTERS.length ? 'Collapse all' : 'Expand all to see details'}
          </button>
        </div>

        {/* Consent Duration Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="p-4" style={{
        backgroundColor: OPENFINANCE_THEME.colors.primary50,
        border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`,
        borderRadius: OPENFINANCE_THEME.radius.lg
      }} data-id="element-531">
          <div className="flex items-start gap-3" data-id="element-532">
            <InfoIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-533" />
            <div className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.primary700
          }} data-id="element-534">
              <div className="font-semibold mb-1" data-id="element-535">Consent Duration</div>
              <div data-id="element-536">
                This consent is valid for{' '}
                <span className="font-bold" data-id="element-537">{durationMonths} months</span> and
                will expire on <span className="font-bold" data-id="element-538">{expiryDate}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Withdrawal Rights */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="p-4" style={{
        backgroundColor: OPENFINANCE_THEME.colors.primary50,
        border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`,
        borderRadius: OPENFINANCE_THEME.radius.lg
      }} data-id="element-539">
          <div className="flex items-start gap-3" data-id="element-540">
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
            backgroundColor: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-541">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-542">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-543" />
              </svg>
            </div>
            <div className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.primary700
          }} data-id="element-544">
              <div className="font-semibold mb-1" data-id="element-545">Your Rights</div>
              <div className="text-xs opacity-80 leading-relaxed" data-id="element-546">
                You can withdraw this consent at any time before it expires by
                visiting your Consent Management settings. Withdrawing consent
                will immediately stop {tppName} from accessing your data.
              </div>
            </div>
          </div>
        </motion.div>

        <ConsentWarning expiryDate={expiryDate} variant="share" durationMonths={durationMonths} data-id="element-547" />

        {/* CDCS-4.1: Hand-off notification with ALTAREQ branding */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="pt-2" data-id="element-548">
          <button onClick={onProceed} className="w-full px-6 py-4 text-white font-semibold transition-all hover:opacity-90" style={{
          background: 'linear-gradient(90deg, #00BCD4 0%, #1976D2 50%, #000000 100%)',
          borderRadius: '50px'
        }} data-id="element-549">
            <div className="flex items-center justify-center gap-3" data-id="element-550">
              <img src="https://cdn.magicpatterns.com/uploads/8Zyr2eTymP8hQsszbaoPhJ/White_Mark.svg" alt="ALTAREQ" className="h-6 w-6 object-contain" data-id="element-551" />
              <span data-id="element-552">Proceed using Al Tareq</span>
            </div>
          </button>
          <div className="text-center mt-3 text-xs" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-553">
            You'll be redirected to {bankName} to authenticate and authorize
            this consent
          </div>
        </motion.div>
      </div>
    </AnimatedScreen>;
}